/** Angular */
import { Injectable } from '@angular/core';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Models */
import { DsLinearLane } from '../models/ds-linear-lanes';
/** Enums */
import { DsLinearEnum } from '../enums/ds-linear.enum';

@Injectable({
  providedIn: 'root',
})
export class DsLinearLanesService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get all lanes (sens et voies).
   *
   * @param prjId Project ID
   * @returns Observable type `<BydModel<DsLinearLane[]>>`
   */
  public getAllLanes(prjId: number): Observable<BydModel<DsLinearLane>> {
    this.path = `/projects/${prjId}/lanes`;
    return this.getModels<DsLinearLane>();
  }

  /**
   * Get Lanes not linked (sens).
   *
   * @param prjId Project ID
   * @returns Observable type `<BydModel<DsLinearLane[]>>`
   */
  public getLanesNotLinked(prjId: number): Observable<BydModel<DsLinearLane>> {
    this.path = `/projects/${prjId}/lanes/parents`;
    return this.getModels<DsLinearLane>();
  }

  /**
   * Get Lanes by parent Id (voies).
   *
   * @param prjId Project ID
   * @param parentId lane Id
   * @returns Observable type `<BydModel<DsLinearLane[]>>`
   */
  public getLanesByParentId(prjId: number, parentId: number): Observable<BydModel<DsLinearLane>> {
    this.path = `/projects/${prjId}/lanes/${parentId}/children`;
    return this.getModels<DsLinearLane>();
  }

  /**
   * Get selected linear work lane links.
   *
   * @param projectId Selected project id
   * @param workId Selected linear work id
   * @returns Observable<BydModel<DsLinearLane>>
   */
  public getLinearWorkLaneLinks(projectId: number, workId: number): Observable<BydModel<DsLinearLane>> {
    this.path = `/projects/${projectId}/works/${workId}/lanes`;
    return this.getModels<DsLinearLane>();
  }

  /**
   * Adds a new lane.
   *
   * @param projectId Project id
   * @param name Lane name
   * @param laneParentId Optional lane parent id
   * @returns Observable<number>
   */
  public addLane(projectId: number, name: string, laneParentId: number): Observable<number> {
    this.path = `/projects/${projectId}/lanes`;
    return this.post<number>({ body: { name, pid: laneParentId } });
  }

  /**
   * Updates lane name and/or lane parent id.
   *
   * @param projectId Project id
   * @param laneId Id of the edited lane
   * @param name New lane name
   * @param laneParentId Optional lane parent id
   * @returns Observable<number>
   */
  public updateLane(projectId: number, laneId: number, name: string, laneParentId: number | null): Observable<number> {
    this.path = `/projects/${projectId}/lanes/${laneId}`;
    return this.put<number>({ body: { name, pid: laneParentId } });
  }

  /**
   * Returns the list of all the lane ids linked to works.
   *
   * @param projectId Project id
   * @param elementsPerPage Maximum number of elements per page
   * @param pageIndex Page index
   * @returns Observable<BydModel<DsLinearLane>>
   */
  public getLaneIdsLinkedToWorks(
    projectId: number,
    elementsPerPage = 1000,
    pageIndex = 0
  ): Observable<BydModel<DsLinearLane>> {
    this.path = `${DsLinearEnum.V1_DS_LINEAR_ENDPOINT}${projectId}/works/lanes`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    return this.getModels<DsLinearLane>({ headers });
  }

  /**
   * Deletes lane.
   *
   * @param projectId Project id
   * @param laneId Id of the lane to delete
   * @returns Observable<boolean>
   */
  public deleteLane(projectId: number, laneId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/lanes/${laneId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsLinearLane instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsLinearLane` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsLinearLane {
    return new DsLinearLane(jsonObject);
  }
}
